import { AxiosInstance } from "axios";
import { BaseClient } from "../base";
import {
  ClientConfig,
  EntityListWithPagination,
  PaginationRequest,
} from "../../types";

import { User, UserFilter } from ".";

export interface IUserClient<TUser extends User = User> {
  /**
   * Gets the list of users in the system.
   * @endpoint `/users`
   * @param pagination Pagination parameters.
   * @param filter Parameters to filter the results.
   * @returns A paginated list of users.
   */
  getUserList: <TUserFilter extends UserFilter = UserFilter>(
    pagination: PaginationRequest,
    filter?: TUserFilter
  ) => Promise<EntityListWithPagination<TUser>>;

  /**
   * Updates the info of the user with given id.
   * @endpoint `/users/:id`
   * @param id User id.
   * @param user User info.
   */
  updateUser: (id: number, user: TUser) => Promise<void>;

  /**
   * Inserts a new user.
   * @endpoint `/users`
   * @param user User info.
   */
  insertUser: (user: TUser) => Promise<void>;

  /**
   * Activates the user with id.
   * @endpoint `/users/:id/activar`
   * @param id User id.
   */
  activateUser: (id: number) => Promise<void>;

  /**
   * Deactivates the user with id.
   * @endpoint `/users/:id`
   * @param id User id.
   */
  deactivateUser: (id: number) => Promise<void>;

  /**
   * Blocks the user with id.
   * @endpoint `/users/:id/bloquear`
   * @param id User id.
   */
  blockUser: (id: number) => Promise<void>;

  /**
   * Unblocks the user with id.
   * @endpoint `/users/:id/desbloquear`
   * @param id User id.
   */
  unblockUser: (id: number) => Promise<void>;
}

export class UserClient<TUser extends User = User>
  extends BaseClient
  implements IUserClient<TUser>
{
  private endpoint = "/users";

  constructor(axiosInstance: AxiosInstance, clientConfig?: ClientConfig) {
    super(axiosInstance, clientConfig);
  }

  private getEndpoint(url: string) {
    return `${this.endpoint}${url}`;
  }

  public async getUserList<TUserFilter extends UserFilter = UserFilter>(
    pagination: PaginationRequest,
    filter?: TUserFilter
  ) {
    return this.get<EntityListWithPagination<TUser>, TUserFilter>(
      this.getEndpoint(""),
      {
        params: filter,
        pagination: pagination,
      }
    );
  }

  public async updateUser(id: number, user: TUser) {
    return this.put<TUser, never, never>(this.getEndpoint(`/${id}`), user, {});
  }

  public async insertUser(user: TUser) {
    return this.post<TUser, never, never>(this.getEndpoint(""), user, {});
  }

  public async deactivateUser(id: number) {
    return this.delete<never, never>(this.getEndpoint(`/${id}`), {});
  }

  public async activateUser(id: number) {
    return this.post<{}, never, never>(
      this.getEndpoint(`/${id}/activar`),
      {},
      {}
    );
  }

  public async blockUser(id: number) {
    return this.post<{}, never, never>(
      this.getEndpoint(`/${id}/bloquear`),
      {},
      {}
    );
  }

  public async unblockUser(id: number) {
    return this.post<{}, never, never>(
      this.getEndpoint(`/${id}/desbloquear`),
      {},
      {}
    );
  }
}
